/**
 * Adapter Registry
 *
 * Maps question types to their corresponding adapter functions.
 * Similar to frontend's questionComponentRegistry pattern.
 */

import { teamPickAdapter } from './adapters/teamPickAdapter';
import { playerPickAdapter } from './adapters/playerPickAdapter';
import { propPickAdapter } from './adapters/propPickAdapter';
import { overUnderPickAdapter } from './adapters/overUnderPickAdapter';
import { tiebreakerAdapter } from './adapters/tiebreakerAdapter';
import { customPickAdapter } from './adapters/customPickAdapter';
import { leaderPickAdapter } from './adapters/leaderPickAdapter';
import type { PreviewQuestionData } from './types';
import type { RenderableQuestionType } from '../questionRenderTypes';

/**
 * Registry of all preview adapters
 *
 * Maps question type strings to their adapter functions.
 * Add new question types here as they're implemented.
 */
export const PREVIEW_ADAPTERS = {
  team: teamPickAdapter,
  player: playerPickAdapter,
  prop: propPickAdapter,
  over_under: overUnderPickAdapter,
  team_over_under: overUnderPickAdapter,
  player_over_under: overUnderPickAdapter,
  tiebreaker: tiebreakerAdapter,
  custom: customPickAdapter,
  player_leader: leaderPickAdapter,
  team_leader: leaderPickAdapter,
  // Main markets (DKPLAT-500): Moneyline/Spread preview as option-picks; Total
  // as a match-level Over/Under.
  moneyline: teamPickAdapter,
  spread: teamPickAdapter,
  total: overUnderPickAdapter,
  // `satisfies` makes this map exhaustive over RenderableQuestionType at compile
  // time: a type added to that union but not here fails to build (and vice versa).
} satisfies Record<RenderableQuestionType, (data: PreviewQuestionData) => unknown>;

/**
 * Supported question types — the single source of truth
 * (see utils/questionRenderTypes.ts).
 */
export type QuestionType = RenderableQuestionType;

/**
 * Check if a question type is supported
 *
 * @param type - Question type string to check
 * @returns True if the type has an adapter registered
 */
export function isValidQuestionType(type: string): type is QuestionType {
  return type in PREVIEW_ADAPTERS;
}

/**
 * Get adapter for a question type
 *
 * @param type - Question type string
 * @returns Adapter function or undefined if not found
 */
export function getAdapter(type: string) {
  return isValidQuestionType(type) ? PREVIEW_ADAPTERS[type] : undefined;
}
